import { lazy, type ComponentType } from 'react';
import { isChunkLoadError } from './chunk-error';
import { markStaleBundle } from './stale-bundle';
import { isBundleStale } from './version-check';

/**
 * Code-split routes, with the deploy-skew case taken out of the error boundary.
 *
 * A chunk that 404s almost always means this tab is running a bundle the last
 * deploy replaced — but offline and a broken deploy produce the identical
 * `TypeError`, so we confirm against `/version.json` before swallowing it.
 * Confirmed skew hands off to `StaleBundleDialog`, which blocks and reloads
 * once; everything else keeps throwing, so the error boundary still renders and
 * Sentry still alerts.
 *
 * Without this the two race: the version poll runs every 5 minutes, a
 * navigation needing a removed chunk happens the instant the user comes back.
 *
 * Exported for the unit test — the two wrappers below are the real entry points.
 */
export async function loadOrMarkStale<T>(
  loader: () => Promise<T>,
): Promise<T | null> {
  try {
    return await loader();
  } catch (error) {
    if (!isChunkLoadError(error) || !(await isBundleStale())) throw error;
    markStaleBundle();
    return null;
  }
}

/**
 * Stands in for the page whose chunk is gone. It renders nothing on purpose:
 * the dialog it hands off to is full-screen and blocking, so there is no gap to
 * fill, and a placeholder would only flash before the reload.
 */
function StaleBundlePlaceholder() {
  return null;
}

/** For pages with a default export. */
export function lazyModule<P>(
  loader: () => Promise<{ default: ComponentType<P> }>,
) {
  return lazy(async (): Promise<{ default: ComponentType<P> }> => {
    const module = await loadOrMarkStale(loader);
    return module ?? { default: StaleBundlePlaceholder };
  });
}

/**
 * `React.lazy` wants a default export; most of our pages are named exports.
 */
export function lazyPage<P, K extends string>(
  loader: () => Promise<Record<K, ComponentType<P>>>,
  key: K,
) {
  return lazy(async (): Promise<{ default: ComponentType<P> }> => {
    const module = await loadOrMarkStale(loader);
    return module
      ? { default: module[key] }
      : { default: StaleBundlePlaceholder };
  });
}
